import Payment from "payment";
import React, { useMemo, useState, useEffect } from "react";
import { Focused } from "react-credit-cards";
import NumberFormat from "react-number-format";
import { Col, Form, FormInstance, Input, Row, Select } from "antd";
import { cnpjInputMask, cpfInputMask } from "../../../../../../utils";
import {
  validationCardShelfLife,
  validationCpf,
  validationCpfCnpj,
  validationCreditCardNumber,
  validationCVV,
  validationFieldRequired,
  validationPhoneRequired,
} from "../../../../../../utils/formValidations";

interface ICardFormProps {
  number: string;
  focused?: Focused;
  form: FormInstance;
  uniqueValue?: number;
  setCvc: (cvc: string) => void;
  setName: (name: string) => void;
  setPhone: (value: string) => void;
  setNumber: (number: string) => void;
  setExpiry: (expiry: string) => void;
  setFocused: (focused: Focused) => void;
  setHolderDocument: (value: string) => void;
  setIsValidCardForm: (isValidCardForm: boolean) => void;
  handleBack: (event: any) => void;
  fixedInstallments?: number;
  signature?: boolean;
  installmentSelected?: number;
  customInstallments?: number;
  plan?: any;
}

// Função para aplicar máscara
const applyMask = (value: string, mask: string): string => {
  let result = '';
  let valueIndex = 0;
  
  for (let i = 0; i < mask.length && valueIndex < value.length; i++) {
    if (mask[i] === '9') {
      if (/\d/.test(value[valueIndex])) {
        result += value[valueIndex];
        valueIndex++;
      }
    } else {
      result += mask[i];
      if (value[valueIndex] === mask[i]) {
        valueIndex++;
      }
    }
  }
  
  return result;
};

// Função para aplicar máscara no número do cartão
const applyCardNumberMask = (value: string): string => {
  // Remove todos os caracteres não numéricos
  const cleanValue = value.replace(/\D/g, '');
  
  // Aplica a máscara com espaços a cada 4 dígitos
  const maskedValue = cleanValue.replace(/(\d{4})(?=\d)/g, '$1 ');
  
  return maskedValue;
};

export const CardForm: React.FC<ICardFormProps> = ({
  form,
  number,
  setCvc,
  setName,
  setPhone,
  setNumber,
  setExpiry,
  setFocused,
  setHolderDocument,
  setIsValidCardForm,
  uniqueValue,
  fixedInstallments,
  handleBack,
  signature,
  installmentSelected,
  customInstallments,
  plan
}) => {
  const [inputType, setInputType] = useState<'cpf' | 'cnpj'>('cnpj');
  const [expiryValue, setExpiryValue] = useState('');
  const [cpfValue, setCpfValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [cardNumberValue, setCardNumberValue] = useState(applyCardNumberMask(number));
  const cardType = useMemo(() => Payment.fns.cardType(number), [number]);

  // Sincroniza o estado do número do cartão quando a prop number mudar
  useEffect(() => {
    setCardNumberValue(applyCardNumberMask(number));
  }, [number]);

  const onValuesChange = (changed: any, values: any) => {
    setCvc(values.cvv ?? "");
    setName(values.holderName ?? "");
    const key = Object.keys(changed)[0];
    setFocused(
      (key === "holderName" ? "name" : key === "cvv" ? "cvc" : key) as Focused
    );
    form.setFieldsValue({
      ...values,
      installments: values.installments ?? 1,
    });

    setTimeout(() => {
      form
        .validateFields()
        .then(() => {
          setIsValidCardForm(true);
        })
        .catch(() => {
          setIsValidCardForm(false);
        });
    }, 500);
  };

  const installmentsOptions = [
    {
      value: 1,
      label: `À vista - R$ ${((plan as any)?.fullPrice || (plan?.price * 12)).toLocaleString("pt-br", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    },
    {
      value: 3,
      label: `3x - R$ ${(((plan as any)?.fullPrice || (plan?.price * 12)) / 3).toLocaleString("pt-br", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    },
    {
      value: 6,
      label: `6x - R$ ${(((plan as any)?.fullPrice || (plan?.price * 12)) / 6).toLocaleString("pt-br", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    },
    {
      value: 12,
      label: `12x - R$ ${(((plan as any)?.fullPrice || (plan?.price * 12)) / 12).toLocaleString("pt-br", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    },
  ];

  if (customInstallments) {
    installmentsOptions.push({
      value: customInstallments,
      label: `${customInstallments}x - R$ ${(((plan as any)?.fullPrice || (plan?.price * 12)) / customInstallments).toLocaleString("pt-br", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    });
  }

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    
    // Limita a 4 dígitos (MM/AA)
    const limitedValue = value.slice(0, 4);
    
    // Aplica a máscara MM/AA
    let maskedValue = '';
    if (limitedValue.length >= 1) {
      maskedValue = limitedValue.slice(0, 2);
    }
    if (limitedValue.length >= 3) {
      maskedValue += '/' + limitedValue.slice(2, 4);
    }
    
    setExpiryValue(maskedValue);
    setExpiry(maskedValue);
    
    // Atualiza o formulário
    form.setFieldsValue({
      expiry: maskedValue
    });
    
    // Valida o campo
    setTimeout(() => {
      form.validateFields(['expiry']).catch(() => {});
    }, 100);
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const mask = inputType === 'cpf' ? cpfInputMask : cnpjInputMask;
    const maskedValue = applyMask(value, mask);
    setCpfValue(maskedValue);
    setHolderDocument(value);
    
    if (value.length < 11) {
      setInputType("cpf");
    } else {
      setInputType("cnpj");
    }
    
    // Atualiza o formulário
    form.setFieldsValue({
      cpf: maskedValue
    });
    
    // Valida o campo
    setTimeout(() => {
      form.validateFields(['cpf']).catch(() => {});
    }, 100);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const maskedValue = applyMask(value, "(99) 99999-9999");
    setPhoneValue(maskedValue);
    setPhone(value);
    
    // Atualiza o formulário
    form.setFieldsValue({
      phone: maskedValue
    });
    
    // Valida o campo
    setTimeout(() => {
      form.validateFields(['phone']).catch(() => {});
    }, 100);
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const maskedValue = applyCardNumberMask(value);
    setCardNumberValue(maskedValue);
    // Remove espaços para enviar apenas números
    const cleanValue = value.replace(/\D/g, '');
    setNumber(cleanValue);
    
    // Atualiza o formulário
    form.setFieldsValue({
      number: maskedValue
    });
    
    // Valida o campo
    setTimeout(() => {
      form.validateFields(['number']).catch(() => {});
    }, 100);
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={onValuesChange}
        initialValues={{
          installments: 1,
        }}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Número do cartão"
              rules={validationCreditCardNumber}
            >
              <Input
                placeholder="Número do cartão"
                className="ant-input ant-input-lg"
                value={cardNumberValue}
                onPaste={(e: any) => {
                  (e.target as HTMLElement).setAttribute("pasted", "true");
                  const value = e.clipboardData.getData("text/plain");
                  const maskedValue = applyCardNumberMask(value);
                  setCardNumberValue(maskedValue);
                  setNumber(value.replace(/\D/g, ''));
                }}
                onChange={handleCardNumberChange}
                maxLength={19} // 16 dígitos + 3 espaços
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Data de vencimento"
              rules={validationCardShelfLife}
            >
              <Input
                value={expiryValue}
                onChange={handleExpiryChange}
                placeholder="MM/AA"
                className="ant-input ant-input-lg"
                maxLength={5} // MM/AA = 5 caracteres
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="cvv"
              rules={validationCVV}
              label="Código de segurança"
            >
              <Input placeholder="Código de segurança" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="installments"
              label="Parcelas"
            >
              <Select
                style={{ width: "auto" }}
                options={installmentsOptions}
                placeholder="Parcelas"
                defaultValue={1}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="holderName"
              label="Nome completo"
              className="nomeCompleto"
              rules={validationFieldRequired}
            >
              <Input placeholder="Nome completo" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="CPF/CNPJ do titular" rules={validationCpfCnpj}>
              <Input
                value={cpfValue}
                onChange={handleCpfChange}
                placeholder="CPF/CNPJ do titular"
                className="ant-input ant-input-lg"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Telefone"
              rules={validationPhoneRequired}
            >
              <Input
                value={phoneValue}
                onChange={handlePhoneChange}
                placeholder="Telefone"
                className="ant-input ant-input-lg"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};